import { useSyncExternalStore } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import { subscribeApiFailures } from '@/lib/api'
import { sessionKeys, type AuthConfig, type Me } from '@/lib/session'

const lifecycles = new WeakMap<QueryClient, ReturnType<typeof createSessionLifecycle>>()

const isSessionQuery = (key: readonly unknown[]) =>
  JSON.stringify(key) === JSON.stringify(sessionKeys.me) || JSON.stringify(key) === JSON.stringify(sessionKeys.config)

export function sessionIdentity(me: Me): string {
  return JSON.stringify([me.id, me.kind, me.authMode, me.role, [...me.capabilities].sort()])
}

/** Keep private data behind a fresh session check whenever cookies or permissions change. */
export function createSessionLifecycle(client: QueryClient) {
  let blocked = false
  let previousIdentity: string | null = null
  let previousConfig: string | null = null
  const listeners = new Set<() => void>()
  const setBlocked = (value: boolean) => {
    if (blocked === value) return
    blocked = value
    for (const listener of listeners) listener()
  }
  const clearPrivateData = () => {
    const filters = { predicate: (query: { queryKey: readonly unknown[] }) => !isSessionQuery(query.queryKey) }
    // Cancellation prevents even a query function that ignores AbortSignal from repopulating the cache.
    void client.cancelQueries(filters)
    client.removeQueries(filters)
    client.getMutationCache().clear()
  }
  const recheck = () => {
    setBlocked(true)
    clearPrivateData()
    void client.cancelQueries({ queryKey: sessionKeys.me, exact: true })
    return client.resetQueries({ queryKey: sessionKeys.me, exact: true })
  }

  const unsubscribeFailures = subscribeApiFailures((path, error) => {
    if (!path.startsWith('/api/')) return
    if (error.status === 401) {
      if (path === '/api/me') {
        setBlocked(true)
        clearPrivateData()
      } else if (!blocked) {
        void recheck()
      }
    } else if (error.status === 403 && path !== '/api/me') {
      void client.invalidateQueries({ queryKey: sessionKeys.me, exact: true })
    }
  })
  const unsubscribeCache = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return
    if (JSON.stringify(event.query.queryKey) === JSON.stringify(sessionKeys.me)) {
      const currentIdentity = sessionIdentity(event.query.state.data as Me)
      if (previousIdentity !== currentIdentity) clearPrivateData()
      previousIdentity = currentIdentity
      setBlocked(false)
    } else if (JSON.stringify(event.query.queryKey) === JSON.stringify(sessionKeys.config)) {
      const config = event.query.state.data as AuthConfig
      const currentConfig = JSON.stringify([config.mode, config.environment])
      if (previousConfig !== null && previousConfig !== currentConfig) void recheck()
      previousConfig = currentConfig
    }
  })

  return {
    getSnapshot: () => blocked,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    recheck,
    dispose: () => {
      unsubscribeFailures()
      unsubscribeCache()
      listeners.clear()
    },
  }
}

export function getSessionLifecycle(client: QueryClient) {
  let lifecycle = lifecycles.get(client)
  if (!lifecycle) {
    lifecycle = createSessionLifecycle(client)
    lifecycles.set(client, lifecycle)
  }
  return lifecycle
}

export function useSessionBlocked() {
  const lifecycle = getSessionLifecycle(useQueryClient())
  return useSyncExternalStore(lifecycle.subscribe, lifecycle.getSnapshot, lifecycle.getSnapshot)
}
